import { useId, useMemo, useState } from 'react';
import {
  AsYouType,
  getCountries,
  getCountryCallingCode,
  getExampleNumber,
  parsePhoneNumberFromString,
  validatePhoneNumberLength,
  type CountryCode,
} from 'libphonenumber-js';
import examples from 'libphonenumber-js/examples.mobile.json';
// #27969: validity is checked against the full metadata, the same the leads endpoint uses; the
// default (min) metadata only checks the length, so a number the API refuses passed here.
import { parsePhoneNumberFromString as parseStrict } from 'libphonenumber-js/max';

/**
 * A phone number with the country it belongs to, matching the picker the admin
 * uses. The value handed out is always E.164 (`+919876543210`), which is the
 * shape the leads endpoint validates, while the person types the number the way
 * they would say it. A number cannot grow past what its country allows.
 */

/** Countries worth offering first; the rest follow alphabetically. */
const PRIORITY: CountryCode[] = ['IN', 'AE', 'GB', 'US', 'SG', 'AU', 'CA'];

const NAMES = new Intl.DisplayNames(['en'], { type: 'region' });

/** The country's flag from its two letters, no image needed. */
function flag(country: CountryCode): string {
  return String.fromCodePoint(...[...country].map((letter) => 0x1f1a5 + letter.charCodeAt(0)));
}

/** Why a number is not dialable, in words a person can act on. */
export function phoneProblem(value: string, country: CountryCode): string | undefined {
  if (!value) return undefined;
  const digits = stripDialCode(value, country).replace(/[^\d]/g, '');
  // Name the country, so it is clear which rules the number is checked against.
  const where = `${NAMES.of(country) ?? country} (+${getCountryCallingCode(country)})`;
  if (digits.length > maxDigits(country)) return `This number is too long for ${where}. Please check the digits.`;
  const length = validatePhoneNumberLength(digits, country);
  if (length === 'TOO_SHORT') return `This number is too short for ${where}. Please enter the full number, e.g. ${sampleNumber(country)}.`;
  if (length === 'TOO_LONG') return `This number is too long for ${where}. Please check the digits.`;
  return parseStrict(value)?.isValid()
    ? undefined
    : `This is not a valid ${where} phone number. It should look like ${sampleNumber(country)}.`;
}

interface PhoneFieldProps {
  /** E.164, e.g. "+919876543210". */
  value: string;
  onChange: (value: string) => void;
  id?: string;
  /** Country selected before anything is typed. */
  defaultCountry?: CountryCode;
  /** Show the number's problem now (the form was submitted), not only once the field is left. */
  showError?: boolean;
  /** A problem found elsewhere (e.g. by the server), shown under the field until the number changes. */
  error?: string;
}

export default function PhoneField({ value, onChange, id, defaultCountry = 'IN', showError = false, error }: PhoneFieldProps) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const [touched, setTouched] = useState(false);

  const parsed = useMemo(() => parsePhoneNumberFromString(value ?? ''), [value]);
  const [picked, setPicked] = useState<CountryCode>(parsed?.country ?? defaultCountry);
  const country = parsed?.country ?? picked;

  // Typed nationally ("98765 43210"), stored internationally ("+919876543210").
  // #27968: only the digits they typed, never the trunk prefix formatNational() adds ("098765 43210"):
  // that "0" appeared by itself once the number was valid, and deleting it brought it straight back.
  const national = parsed ? parsed.nationalNumber : stripDialCode(value, country);

  // The dial code leads so it survives the truncation a narrow control forces.
  const countries = useMemo(() => {
    const rest = getCountries()
      .filter((code) => !PRIORITY.includes(code))
      .sort((a, b) => (NAMES.of(a) ?? a).localeCompare(NAMES.of(b) ?? b));
    return [...PRIORITY, ...rest].map((code) => ({
      code,
      label: `${flag(code)} +${getCountryCallingCode(code)}  ${NAMES.of(code) ?? code}`,
    }));
  }, []);

  function emit(nextCountry: CountryCode, typed: string) {
    const digits = capped(nationalDigits(typed, nextCountry), nextCountry);
    if (!digits) {
      onChange('');
      return;
    }
    const number = parsePhoneNumberFromString(digits, nextCountry);
    onChange(number ? number.number : `+${getCountryCallingCode(nextCountry)}${digits}`);
  }

  // Only complain once they have moved on; not while they are still typing.
  const problem = (touched || showError ? phoneProblem(value, country) : undefined) ?? error;

  return (
    <>
      <label className="lbl" htmlFor={inputId}>Phone number</label>
      <div className="phone-row">
        {/* #27903: closed, the control shows only the flag and dial code, which always fit; the
            full country names are in the list it opens. The native select sits on top, invisible,
            so the list, keyboard and screen readers work as before. */}
        <div className="phone-country">
          <span className="field phone-country-face" aria-hidden="true">
            {flag(country)} +{getCountryCallingCode(country)}
          </span>
          <select
            className="phone-country-select"
            aria-label="Country calling code"
            value={country}
            onChange={(e) => {
              const next = e.target.value as CountryCode;
              setPicked(next);
              emit(next, national);
            }}
          >
            {countries.map((option) => (
              <option key={option.code} value={option.code}>{option.label}</option>
            ))}
          </select>
        </div>
        <input
          className={problem ? 'field phone-number bad' : 'field phone-number'}
          id={inputId}
          name="phone"
          type="tel"
          inputMode="tel"
          autoComplete="tel-national"
          placeholder={example(country)}
          value={new AsYouType(country).input(national)}
          aria-invalid={problem ? true : undefined}
          aria-describedby={problem ? `${inputId}-err` : undefined}
          onChange={(e) => emit(country, e.target.value)}
          onBlur={() => setTouched(true)}
        />
      </div>
      {problem && <p className="field-err" id={`${inputId}-err`}>{problem}</p>}
    </>
  );
}

/**
 * How many digits a mobile number has in this country — ten in India and the
 * US, nine in the UAE, eight in Singapore. Taken from the country's own example
 * number, because a few countries also allow longer service numbers that nobody
 * types into a "phone" field.
 */
function maxDigits(country: CountryCode): number {
  return getExampleNumber(country, examples)?.nationalNumber.length ?? 15;
}

/**
 * The digits that actually belong to the number, without the trunk prefix some
 * countries write nationally ("098765 43210" in India is ten digits, not eleven).
 */
function nationalDigits(typed: string, country: CountryCode): string {
  const raw = typed.replace(/[^\d]/g, '');
  return parsePhoneNumberFromString(raw, country)?.nationalNumber ?? raw;
}

/** Refuses the digit that would take the number past its country's length. */
function capped(digits: string, country: CountryCode): string {
  return digits.slice(0, maxDigits(country));
}

/** A real-looking local number for this country, for the error messages (e.g. "406 12 345" for Norway). */
function sampleNumber(country: CountryCode): string {
  return getExampleNumber(country, examples)?.formatNational() ?? 'the full local number';
}

/** What a number from this country looks like, as placeholder text. */
function example(country: CountryCode): string {
  return country === 'IN' ? '98765 43210' : country === 'US' || country === 'CA' ? '(555) 123-4567' : 'Phone number';
}

/** A raw value that is not yet a valid number: drop the dial code so it reads nationally. */
function stripDialCode(value: string, country: CountryCode): string {
  const dial = `+${getCountryCallingCode(country)}`;
  return value.startsWith(dial) ? value.slice(dial.length) : value.replace(/^\+/, '');
}
